import { useEffect, useState } from 'react'
import { FileText, Image as ImageIcon } from 'lucide-react'
import * as filesApi from '../api'
import * as tasksApi from '../../../tasks/api'
import { ImageLightbox } from '../../../tasks/components/files/ImageLightbox'

/**
 * Klikalna miniatura pliku - osobne URL dla miniaturki (szybkie) i podglądu pełnoekranowego.
 *
 * Reguła:
 *  - Miniatura (48x48): preferuje {@code thumbnailUrl} z list response (task source - eager 200x200);
 *    dla source='contract' lazy fetcha {@code /thumbnail-url} (backend generuje na żądanie + cache w S3).
 *  - Lightbox (po kliknięciu): fetcha pełny {@code /download-url} - presigned URL bezpośrednio na S3,
 *    co działa w {@code <img src>} (nie ma 302 problemu z cross-origin + credentials).
 *
 * Routing przez {@code source}:
 *  - 'contract' -> easycrm /api/contracts/{id}/files/{fid}/(thumbnail|download)-url
 *  - 'task' -> easycrm proxy /api/tasks/{taskId}/files/{fid}/(thumbnail|download)-url
 */
export function FileThumbnail({
  contractId, fileId, isImage, source, taskRefId,
  thumbnailUrl, filename,
}: {
  contractId: number
  fileId: number
  isImage: boolean
  source?: 'contract' | 'task'
  taskRefId?: number
  thumbnailUrl?: string | null
  filename?: string
}) {
  const [thumbSrc, setThumbSrc] = useState<string | null>(thumbnailUrl ?? null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  // Załaduj URL miniaturki (jeżeli backend już nie dał thumbnailUrl w list response).
  useEffect(() => {
    if (!isImage || thumbnailUrl) return
    let cancelled = false
    const promise = source === 'task' && taskRefId != null
      ? tasksApi.getThumbnailUrl(taskRefId, fileId)
      : filesApi.getThumbnailUrl(contractId, fileId, 200)
    promise
      .then((d) => { if (!cancelled) setThumbSrc(d.url) })
      .catch(() => { /* fallback do placeholdera */ })
    return () => { cancelled = true }
  }, [contractId, fileId, isImage, thumbnailUrl, source, taskRefId])

  const openPreview = async () => {
    setLightboxOpen(true)
    setLightboxUrl(null)
    try {
      const { url } = source === 'task' && taskRefId != null
        ? await tasksApi.getDownloadUrl(taskRefId, fileId)
        : await filesApi.getDownloadUrl(contractId, fileId)
      setLightboxUrl(url)
    } catch {
      setLightboxOpen(false)
    }
  }

  const closePreview = () => {
    setLightboxOpen(false)
    setLightboxUrl(null)
  }

  if (!isImage) {
    return (
      <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-400">
        <FileText className="size-5" />
      </div>
    )
  }
  if (!thumbSrc) {
    return (
      <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-400">
        <ImageIcon className="size-5" />
      </div>
    )
  }
  return (
    <>
      <button
        type="button"
        onClick={openPreview}
        className="block size-12 cursor-zoom-in overflow-hidden rounded"
        title="Powiększ"
      >
        <img
          src={thumbSrc}
          alt=""
          className="size-full bg-slate-100 object-cover transition hover:opacity-80"
          loading="lazy"
        />
      </button>
      {lightboxOpen && (
        <ImageLightbox
          src={lightboxUrl}
          filename={filename}
          onClose={closePreview}
        />
      )}
    </>
  )
}
